import React, { useCallback, useEffect, useState } from 'react';

import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import PrimaryTabbedComponent from '../../shared/components/layout/PrimaryTabbedComponent';
import SecondaryTabbedComponent from '../../shared/components/layout/SecondaryTabbedComponent';
import SalesTargets from './SalesTargets';
import SalesReceivables from './SalesReceivables';
import SalesRevenue from './SalesRevenue';
import SalesInventoryReports from './SalesInventoryReport';
import SalesInventoryDetails from './SalesInventoryDetails';
import SalesReportCsvDownloadButton from '../components/SalesReportCsvDownloadButton';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const SalesReports = () => {
  const userId = Cookies.get('userId');
  /* 
    Local state 
  */
  const [primaryTab, setPrimaryTab] = useState('Targets');
  const [revenueSubTab, setRevenueSubTab] = useState('Blocks wise');
  const [revenueProjectId, setRevenueProjectId] = useState(null);
  const [inventoryProjectId, setInventoryProjectId] = useState(null);

  /* 
    Meta data
  */
  const tabbedComponentMetaData = {
    tabs: ['Targets', 'Receivables', 'Revenue Report', 'Inventory Report', 'Inventory detail Report']
  };


  // Track page view
  useEffect(() => {
    mixpanel.track('Sales Report Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    const savedPrimaryTab = localStorage.getItem('primaryTab');
    if (savedPrimaryTab) setPrimaryTab(savedPrimaryTab);
  }, []);

  useEffect(() => {
    const clearStorage = () => {
      localStorage.removeItem('primaryTab');
    };

    // Clear on unmount
    return () => {
      clearStorage();
    };
  }, []);

  /* 
    Event handlers
  */
  const handleChangePrimaryTab = useCallback((tabName) => {
    setPrimaryTab(tabName);
    localStorage.setItem('primaryTab', tabName); // Save to localStorage
  }, []);

  const { collapsed } = useSidebar();

  const primaryEndAction =
    primaryTab === 'Receivables' ? (
      <SalesReportCsvDownloadButton variant="receivables" />
    ) : primaryTab === 'Revenue Report' ? (
      <div className="flex items-center gap-3 sm:gap-4 flex-wrap shrink-0 justify-end">
        <SecondaryTabbedComponent
          compact
          data={{ tabs: ['Blocks wise', 'Unit wise'] }}
          activePrimary={revenueSubTab}
          onChangePrimaryTab={setRevenueSubTab}
        />
        <span className="hidden sm:block h-9 w-px shrink-0 bg-stone-200" aria-hidden />
        <SalesReportCsvDownloadButton
          variant="revenue"
          revenueSubTab={revenueSubTab}
          selectedProjectId={revenueProjectId}
        />
      </div>
    ) : primaryTab === 'Inventory Report' ? (
      <SalesReportCsvDownloadButton variant="inventory" selectedProjectId={inventoryProjectId} />
    ) : null;

  /* 
    JSX 
  */
  return (
    <>
      <Layout>
        <div className="flex h-[100dvh] min-h-0 flex-col overflow-hidden">
          <div className="z-50 shrink-0">
            <Header data={{ name: 'Sales Reports' }} backArrow={true} />
          </div>
          <div
            className={`flex min-h-0 flex-1 flex-col transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
            <div className="z-10 w-full shrink-0 bg-white">
              <PrimaryTabbedComponent
                data={tabbedComponentMetaData}
                activePrimary={primaryTab}
                onChangePrimaryTab={(tab) => handleChangePrimaryTab(tab)}
                endAction={primaryEndAction}
              />
            </div>
            {primaryTab === 'Targets' && (
              <div className="flex min-h-0 w-full flex-1 flex-col overflow-auto">
                <SalesTargets key={'salesTarget'} />
              </div>
            )}
            {primaryTab === 'Receivables' && (
              <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
                <SalesReceivables key={'salesReceivables'} />
              </div>
            )}
            {primaryTab === 'Revenue Report' && (
              <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
                <SalesRevenue
                  key={'salesRevenue'}
                  subTab={revenueSubTab}
                  selectedProjectId={revenueProjectId}
                  onSelectedProjectIdChange={setRevenueProjectId}
                />
              </div>
            )}
            {primaryTab === 'Inventory Report' && (
              <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
                <SalesInventoryReports
                  key={'salesInventoryReports'}
                  selectedProjectId={inventoryProjectId}
                  onSelectedProjectIdChange={setInventoryProjectId}
                />
              </div>
            )}
            {primaryTab === 'Inventory detail Report' && (
              <div className="flex min-h-0 w-full flex-1 flex-col overflow-auto">
                <SalesInventoryDetails key={'salesInventoryDetails'} />
              </div>
            )}
          </div>
        </div>
      </Layout>
    </>
  );
};

export default SalesReports;
